<script lang="ts">
	import { createEventDispatcher, onMount } from 'svelte';
	import { field } from 'svelte-forms';
	import { required } from 'svelte-forms/validators';
	import { getTranslate } from '@tolgee/svelte';

	import InputWithLabel from '$lib/components/Form/Input/InputWithLabel.svelte';
	import { customForm } from '$lib/types/form';
	import Button from '../Button/Button.svelte';
	import Checkbox from './Input/Checkbox.svelte';
	import { page } from '$app/stores';
	import { browser } from '$app/environment';

	const { t } = getTranslate();
	const dispatch = createEventDispatcher();
	let { supabase } = $page.data;
	$: ({ supabase } = $page.data);

	let loading: boolean = false;

	// TODO: Maybe enable constant validity checking after touch of formfield on username and email.
	const username = field('username', '', [required()]);
	const receive_ebook = field('receive_ebook', false, []);
	const newsletter_accepted = field('newsletter_accepted', false, []);
	const signUpForm = customForm(username, receive_ebook, newsletter_accepted);

	let serverError: string = '';

	const handleSubmit = async () => {
		loading = true;
		await signUpForm.internalForm.validate();

		try {
			if (!$signUpForm.valid) {
				throw 'username form is invalid';
			}

			const userId = $page.data.session?.user.id;
			try {
				if (!userId && $username.value) {
					throw (
						'Error: No userId' +
						userId +
						' or option ' +
						$username.value +
						' to update profile - ERROR Code 105'
					);
				} else if (userId) {
					await supabase
						.from('profiles')
						.update({
							username: $username.value,
							receive_ebook: $receive_ebook.value,
							newsletter_accepted: $newsletter_accepted.value
						})
						.eq('id', userId);
					dispatch('success');
				}
			} catch (error) {
				dispatch('error', { error });
			}
		} catch (error: any) {
			console.error('ERROR while signing up', { error });
			const { message } = error;
			serverError = message;
			dispatch('error', error);
		} finally {
			loading = false;
		}
	};

	async function updateProfileBeforeSignUpPath() {
		if (!browser) return;

		const userId = $page.data.session?.user?.id;
		if (!userId) {
			throw 'Error: No userId found to update user profile - ERROR Code 104';
		} else {
			const beforeSignUpPath = localStorage.getItem('dsd_signUp_course') ?? '';
			let previousBeforeSignUpPath: string | null = 'placeholder';

			try {
				const previousBeforeSignUpPathData = await supabase
					.from('profiles')
					.select('before_signup_path')
					.eq('id', userId);

				if (!previousBeforeSignUpPathData.data) return;

				previousBeforeSignUpPath = previousBeforeSignUpPathData?.data[0]?.before_signup_path;
			} catch (error) {
				console.error('Error: While getting profile - ERROR Code 104a: ', { error });
			}

			if (!previousBeforeSignUpPath) {
				try {
					await supabase
						.from('profiles')
						.update({ before_signup_path: beforeSignUpPath })
						.eq('id', userId);
				} catch (error) {
					console.error('Error: While updating profile - ERROR Code 104b: ', { error });
				}
			}
		}
	}

	onMount(() => {
		updateProfileBeforeSignUpPath();
	});
</script>

<form class="space-y-4 lg:space-y-6" on:submit|preventDefault={handleSubmit}>
	<InputWithLabel
		type="text"
		id="username"
		placeholder={$t('signUpForm.username.placeholder')}
		bind:value={$username.value}
		errorClass="border-error"
		errorMessage={$t('form_usernameRequiredErrorMessage')}
		hasError={$signUpForm.hasError('username.required')}
		data-test="__signup-username"
	/>
	<div class="flex flex-col gap-2">
		<Checkbox
			id="ebook"
			bind:checked={$receive_ebook.value}
			ariaLabel="ebook-checkbox"
			additionalClass={{ label: 'text-gray-500 dark:text-gray-300' }}
		>
			{@html $t('receive_ebook')}
		</Checkbox>
		<Checkbox
			id="newsletter"
			bind:checked={$newsletter_accepted.value}
			ariaLabel="newsletter-checkbox"
			additionalClass={{ label: 'text-gray-500 dark:text-gray-300' }}
		>
			{@html $t('signup_newsletter')}
		</Checkbox>
	</div>

	{#if serverError}
		<div class="text-error">{serverError}</div>
	{/if}

	<div class="text-center">
		<Button additionalClass="mt-4" type="submit" data-test="__signup-submit"
			>{@html $t('signup.formSocialUsername.submit_button')}</Button
		>
	</div>
</form>
